<template>
  <div>
    <!-- Filters -->
    <v-row class="mb-4">
      <v-col cols="12" md="4">
        <v-text-field
          v-model="filters.deviceId"
          :label="$t('global.adminBilling.filterByDeviceId')"
          clearable
          outlined
          dense
          type="number"
          @change="resetAndFetch"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="filters.status"
          :label="$t('global.adminBilling.filterByStatus')"
          :items="statusOptions"
          clearable
          outlined
          dense
          @change="resetAndFetch"
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="itemsPerPage"
          :label="$t('global.adminBilling.itemsPerPage')"
          :items="[10, 20, 50, 100]"
          outlined
          dense
          @change="resetAndFetch"
        />
      </v-col>
    </v-row>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="transactions"
      :loading="$apollo.loading"
      :server-items-length="totalCount"
      :options.sync="options"
      :items-per-page="itemsPerPage"
      :footer-props="{
        'items-per-page-options': [10, 20, 50, 100],
      }"
      class="elevation-1"
      @update:page="handlePageChange"
      @update:items-per-page="handleItemsPerPageChange"
    >
      <!-- Transaction ID -->
      <template v-slot:item.id="{ item }">
        <div class="text-body-2 font-weight-medium">{{ item.id.substring(0, 8) }}...</div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on" @click="copyToClipboard(item.id)">mdi-content-copy</v-icon>
          </template>
          <span>{{ $t('global.clickToCopy') }}</span>
        </v-tooltip>
      </template>

      <!-- PayGate ID -->
      <template v-slot:item.paygateTransactionId="{ item }">
        <code v-if="item.paygateTransactionId" class="text-caption">
          {{ item.paygateTransactionId }}
        </code>
        <span v-else class="grey--text">N/A</span>
      </template>

      <!-- Status -->
      <template v-slot:item.status="{ item }">
        <v-chip :color="getStatusColor(item.status)" text-color="white" small>
          {{ item.status }}
        </v-chip>
      </template>

      <!-- Amount -->
      <template v-slot:item.amountInCents="{ item }">
        <div class="text-body-2">
          {{ formatAmount(item.amountInCents, item.currency) }}
        </div>
      </template>

      <!-- Device -->
      <template v-slot:item.subscriptions="{ item }">
        <span v-if="!item.subscriptions.length" class="grey--text">N/A</span>
        <div v-else-if="item.subscriptions.length === 1" class="text-body-2">
          {{ subscriptionDeviceLabel(item.subscriptions[0]) }}
        </div>
        <!-- Wholesale invoices cover many devices -->
        <v-tooltip v-else bottom max-width="320">
          <template v-slot:activator="{ on }">
            <v-chip small v-on="on">{{ item.subscriptions.length }} devices</v-chip>
          </template>
          <div v-for="subscription in item.subscriptions" :key="subscription.subscriptionId">
            {{ subscriptionDeviceLabel(subscription) }}
          </div>
        </v-tooltip>
      </template>

      <!-- Created -->
      <template v-slot:item.createdAt="{ item }">
        <div class="text-body-2">
          {{ formatDateTime(item.createdAt) }}
        </div>
      </template>

      <!-- Invoice -->
      <template v-slot:item.invoiceUrl="{ item }">
        <v-tooltip bottom v-if="item.invoiceUrl">
          <template v-slot:activator="{ on }">
            <v-icon small v-on="on" @click="openInvoice(item.invoiceUrl)">
              mdi-file-document-outline
            </v-icon>
          </template>
          <span>{{ $t('global.adminBilling.viewInvoice') }}</span>
        </v-tooltip>
        <span v-else class="grey--text">-</span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <v-menu v-if="debuggableDevices(item).length > 1" offset-y max-height="300">
          <template v-slot:activator="{ on }">
            <v-btn small color="info" v-on="on">
              Show Debug
              <v-icon right small>mdi-menu-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item
              v-for="subscription in debuggableDevices(item)"
              :key="subscription.subscriptionId"
              @click="showDebug(subscription)"
            >
              <v-list-item-title>{{ subscriptionDeviceLabel(subscription) }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <v-btn
          v-else
          small
          color="info"
          :disabled="!debuggableDevices(item).length"
          @click="showDebug(debuggableDevices(item)[0])"
        >
          Show Debug
        </v-btn>
      </template>
    </v-data-table>

    <!-- Summary Statistics -->
    <v-row class="mt-4" v-if="totalCount > 0">
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4 primary--text">{{ totalCount }}</div>
            <div class="text-caption">{{ $t('global.adminBilling.totalTransactions') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4 success--text">{{ paidCount }}</div>
            <div class="text-caption">{{ $t('global.adminBilling.paidTransactions') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4 warning--text">{{ pendingCount }}</div>
            <div class="text-caption">{{ $t('global.adminBilling.pendingTransactions') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="3">
        <v-card outlined>
          <v-card-text class="text-center">
            <div class="text-h4 error--text">{{ failedCount }}</div>
            <div class="text-caption">{{ $t('global.adminBilling.failedTransactions') }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { adminBillingTransactions } from '@/graphql/query/adminBillingTransactions'

export default {
  name: 'BillingTransactionTable',

  data() {
    return {
      transactions: [],
      totalCount: 0,
      totalPages: 0,
      currentPage: 1,
      itemsPerPage: 20,
      options: {},
      filters: {
        deviceId: null,
        status: null,
      },
      statusOptions: [
        { text: 'Paid', value: 'PAID' },
        { text: 'Pending', value: 'PENDING' },
        { text: 'Failed', value: 'FAILED' },
        { text: 'Cancelled', value: 'CANCELLED' },
        { text: 'Invoiced (wholesale)', value: 'SEND' },
      ],
    }
  },

  computed: {
    headers() {
      return [
        { text: this.$t('global.adminBilling.transactionId'), value: 'id', width: 150 },
        {
          text: this.$t('global.adminBilling.paygateId'),
          value: 'paygateTransactionId',
          width: 150,
        },
        { text: this.$t('global.adminBilling.status'), value: 'status', width: 120 },
        { text: this.$t('global.adminBilling.amount'), value: 'amountInCents', width: 120 },
        {
          text: this.$t('global.adminBilling.device'),
          value: 'subscriptions',
          width: 200,
          sortable: false,
        },
        { text: this.$t('global.adminBilling.created'), value: 'createdAt', width: 180 },
        {
          text: this.$t('global.adminBilling.invoice'),
          value: 'invoiceUrl',
          width: 100,
          sortable: false,
        },
        { text: '', value: 'actions', sortable: false, width: 130 },
      ]
    },

    paidCount() {
      return this.transactions.filter((t) => t.status === 'PAID').length
    },

    pendingCount() {
      return this.transactions.filter((t) => t.status === 'PENDING').length
    },

    failedCount() {
      return this.transactions.filter((t) => t.status === 'FAILED' || t.status === 'CANCELLED')
        .length
    },
  },

  apollo: {
    adminBillingTransactions: {
      query: adminBillingTransactions,
      // Debounces the automatic refetch vue-apollo already triggers whenever a reactive
      // dependency read inside variables() (filters, currentPage, itemsPerPage) changes -
      // do not also call refresh()/refetch() manually on those same changes, that fires it twice.
      debounce: 500,
      variables() {
        return {
          limit: this.itemsPerPage,
          page: this.currentPage,
          deviceId: this.filters.deviceId ? parseInt(this.filters.deviceId) : null,
          status: this.filters.status,
        }
      },
      update(data) {
        const result = data.adminBillingTransactions
        this.transactions = result.transactions || []
        this.totalCount = result.totalCount || 0
        this.totalPages = result.totalPages || 0
        this.currentPage = result.page || 1
        return result
      },
      error(error) {
        this.$toast.error('Failed to load transactions: ' + error.message)
      },
    },
  },

  watch: {
    options: {
      handler() {
        // Handle v-data-table options changes
        if (this.options.page !== this.currentPage) {
          this.currentPage = this.options.page
        }
        if (this.options.itemsPerPage !== this.itemsPerPage) {
          this.itemsPerPage = this.options.itemsPerPage
        }
      },
      deep: true,
    },
  },

  methods: {
    // Public method for parent to refresh data
    refresh() {
      if (this.$apollo.queries.adminBillingTransactions) {
        this.$apollo.queries.adminBillingTransactions.refetch()
      }
    },

    resetAndFetch() {
      // Just resets the page - itemsPerPage/filters were already updated by v-model, and
      // vue-apollo's variables() watcher auto-refetches on any of these reactive changes.
      this.currentPage = 1
      this.options.page = 1
    },

    handlePageChange(page) {
      this.currentPage = page
    },

    handleItemsPerPageChange(value) {
      this.itemsPerPage = value
      this.currentPage = 1
      this.options.page = 1
    },

    getStatusColor(status) {
      switch (status) {
        case 'PAID':
          return 'success'
        case 'PENDING':
          return 'warning'
        case 'SEND':
          return 'info'
        case 'FAILED':
        case 'CANCELLED':
          return 'error'
        default:
          return 'grey'
      }
    },

    formatAmount(amountInCents, currency) {
      const amount = amountInCents / 100
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
      }).format(amount)
    },

    formatDateTime(dateInput) {
      if (!dateInput) return '-'

      // Numeric timestamps (seconds or milliseconds)
      if (typeof dateInput === 'number') {
        const ms = dateInput < 1e12 ? dateInput * 1000 : dateInput
        return new Date(ms).toLocaleString()
      }

      // Numeric string timestamps
      if (typeof dateInput === 'string' && /^\d+$/.test(dateInput)) {
        const num = parseInt(dateInput, 10)
        const ms = num < 1e12 ? num * 1000 : num
        return new Date(ms).toLocaleString()
      }

      // Try native parsing first
      let d = new Date(dateInput)
      if (!isNaN(d)) return d.toLocaleString()

      // Normalize common DB format: 'YYYY-MM-DD HH:mm:ss[.ffffff]' -> ISO
      if (typeof dateInput === 'string') {
        let normalized = dateInput.replace(' ', 'T')

        // Truncate microseconds to milliseconds for JS Date
        normalized = normalized.replace(/(\.\d{3})\d+/, '$1')

        // Append 'Z' if no timezone info present
        if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
          normalized += 'Z'
        }

        d = new Date(normalized)
        if (!isNaN(d)) return d.toLocaleString()
      }

      // Fallback: return as-is to avoid 'Invalid Date'
      return String(dateInput)
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
      this.$toast.success(this.$t('global.copiedToClipboard'))
    },

    openInvoice(invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener')
    },

    subscriptionDeviceLabel(subscription) {
      if (subscription.deviceName) return subscription.deviceName
      return subscription.deviceId ? `Device ID: ${subscription.deviceId}` : 'N/A'
    },

    debuggableDevices(item) {
      return item.subscriptions.filter((subscription) => subscription.deviceId)
    },

    showDebug(subscription) {
      // adminBillingDebug expects a relay global ID (same encoding adminBillingDevices
      // already returns as `id`), but this query only gives us the raw device id.
      this.$emit('show-debug', {
        id: btoa(`Device:${subscription.deviceId}`),
        customName: subscription.deviceName,
      })
    },
  },
}
</script>

<style scoped>
code {
  background-color: #f5f5f5;
  padding: 2px 4px;
  border-radius: 3px;
  font-family: 'Roboto Mono', monospace;
}
</style>
